import { Badge, SxProps, Theme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import React, { FC } from 'react';
import { useGuideCmsContext } from '../GuideCmsContext';
import { Forum, Help, Image, Link, Spellcheck, Toc, ViewSidebar } from '@mui/icons-material';
import { useEditorState } from '@tiptap/react';
import { getDictionaryMatches } from '../tiptap/extensions/plugins/DictionaryHighlight';
import styled from '@emotion/styled';
import { useFeatureFlags } from '../../../unleash';
import { useReferencesContext } from './references/ReferencesContext';
import { getImageFiguresWithInstances, getMissingFileWarning } from '../figures/figureHelper';
import { useThreadsState } from './tiptapComments/context';

const StyledSidebarButtons = styled.div`
  padding-right: 8px;
`;

const toggleButtonStyle: SxProps<Theme> = {
  backgroundColor: '#FFF',
};

export const SidebarButtons: FC = () => {
  const { sidebarType, setSidebarType, isSidebarOpen, toggleSidebar, editor } = useGuideCmsContext();
  const { cmsComments, dictionary } = useFeatureFlags();
  const rejectedTermCount =
    useEditorState({ editor, selector: (ctx) => (ctx.editor ? getDictionaryMatches(ctx.editor.state, 'rejected').length : 0) }) ?? 0;
  const { hasBrokenReferences, hasCandidates, hasMissingReferences } = useReferencesContext();
  const hasIssuesWithReferences = hasBrokenReferences || hasCandidates || hasMissingReferences;
  const hasFigureWarnings = editor ? getImageFiguresWithInstances(editor).some((f) => getMissingFileWarning(f) !== null) : false;
  const { threads } = useThreadsState();
  const hasActiveComments = threads.some((thread) => !thread.resolvedAt);

  return (
    <StyledSidebarButtons>
      <div>
        <ToggleButton
          title={isSidebarOpen ? 'Skjul sidemeny' : 'Vis sidemeny'}
          sx={{ marginBottom: 2, ...toggleButtonStyle }}
          size="small"
          value=""
          selected={isSidebarOpen}
          onChange={() => toggleSidebar()}>
          <ViewSidebar fontSize="small" />
        </ToggleButton>
      </div>

      <ToggleButtonGroup
        orientation="vertical"
        value={sidebarType}
        exclusive
        onChange={(_e, type) => {
          if (!isSidebarOpen) {
            toggleSidebar();
          }
          if (type !== null && sidebarType !== type) {
            setSidebarType(type);
          }
        }}>
        <ToggleButton title="Innholdsfortegnelse" size="small" value="toc" selected={isSidebarOpen && sidebarType === 'toc'} sx={toggleButtonStyle}>
          <Toc fontSize="small" />
        </ToggleButton>
        {cmsComments && (
          <Badge variant="dot" color="error" overlap="circular" invisible={!hasActiveComments}>
            <ToggleButton
              title="Kommentarer"
              size="small"
              value="comments"
              selected={isSidebarOpen && sidebarType === 'comments'}
              sx={toggleButtonStyle}>
              <Forum fontSize="small" />
            </ToggleButton>
          </Badge>
        )}
        <Badge variant="dot" color="error" overlap="circular" invisible={!hasIssuesWithReferences}>
          <ToggleButton
            title="Referanser"
            size="small"
            value="references"
            selected={isSidebarOpen && sidebarType === 'references'}
            sx={toggleButtonStyle}>
            <Link fontSize="small" />
          </ToggleButton>
        </Badge>

        <Badge variant="dot" color="error" overlap="circular" invisible={!hasFigureWarnings}>
          <ToggleButton title="Figurer" size="small" value="figures" selected={isSidebarOpen && sidebarType === 'figures'} sx={toggleButtonStyle}>
            <Image fontSize="small" />
          </ToggleButton>
        </Badge>

        {dictionary && (
          <Badge variant="dot" color="error" overlap="circular" invisible={rejectedTermCount === 0}>
            <ToggleButton
              title="Ordliste"
              size="small"
              value="dictionary"
              selected={isSidebarOpen && sidebarType === 'dictionary'}
              sx={toggleButtonStyle}>
              <Spellcheck fontSize="small" />
            </ToggleButton>
          </Badge>
        )}

        <ToggleButton title="Hjelp" size="small" value="help" selected={isSidebarOpen && sidebarType === 'help'} sx={toggleButtonStyle}>
          <Help fontSize="small" />
        </ToggleButton>
      </ToggleButtonGroup>
    </StyledSidebarButtons>
  );
};
